import { useState, useEffect } from 'react';
import { useSingletonAsync, singletonCache } from "./useSingletonAsync";

import { API_BASE } from '../config';
import { normalizeLabel } from '../utils/labelNormalization';

export interface OptionItem {
  label: string;
  value: string;
}

export interface PropertySpec {
  depends_on: Record<string, unknown> | string | null;
  key: string;
  label: string;
  control: string;
  value_type?: string;
  options?: OptionItem[];
  options_source?: string;
  default?: string | number | boolean;
  tooltip?: string;
}

export interface DataTypeSpec {
  id: string;
  label: string;
  category?: string;
  strategy_grammars?: string[];
  properties: PropertySpec[];
  description?: string;
  data_nature?: string;  // Nature metadata from backend (price, volume, fundamental, signal, generic)
  time_type?: string;
  translation?: {
    capability: string;
    label: string;
    can_translate_now: boolean;
    can_be_pnl_primary: boolean;
    can_be_meepmeep_source?: boolean;
    requires_bundle_snapshot: boolean;
    backtest_only: boolean;
    message: string;
  };
  time_profile?: {
    kind?: string;
    resolution?: string;
    preferred_combine_on?: string;
    label?: string;
  };
  naming_template?: string | null;
}


let globalOptionsCache: Record<string, OptionItem[]> = {};
let cacheListeners: Array<() => void> = [];

function notifyCacheListeners() {
  cacheListeners.forEach(l => l());
}

const optionsPromiseCache = new Map<string, Promise<OptionItem[]>>();

export function useDataTypes() {
  const { data, loading, error } = useSingletonAsync<DataTypeSpec[]>(
    "data_types",
    async () => {
      const res = await fetch(`${API_BASE}/data_types`);
      if (!res.ok) throw new Error(`GET /data_types → HTTP ${res.status}`);
      const json = await res.json();
      return (json.data_types as DataTypeSpec[]).map(dt => ({
        ...dt,
        label: normalizeLabel(dt.label)
      }));
    }
  );

  const [cacheVersion, setCacheVersion] = useState(0);

  useEffect(() => {
    const l = () => setCacheVersion(v => v + 1);
    cacheListeners.push(l);
    return () => {
      cacheListeners = cacheListeners.filter(x => x !== l);
    };
  }, []);

  function getDataTypeById(id: string): DataTypeSpec | undefined {
    return data?.find((dt) => dt.id === id);
  }


  return {
    dataTypes: data ?? [],
    loading,
    error,
    getDataTypeById,
    getOptions,
    getOptionsFromCache,
    _v: cacheVersion
  };
}

function normalizeOptions(list: Record<string, unknown>[]): OptionItem[] {
  return list.map((o) => {
    const rawLabel = typeof o.label === 'string' ? o.label : String(o.value);
    return {
      label: normalizeLabel(rawLabel),
      value: String(o.value),
    };
  });
}

export function coerceForSpec(spec: PropertySpec, rawValue: string | number | boolean): string | number | boolean {
  if (spec.value_type === "int") {
    return typeof rawValue === 'string' ? parseInt(rawValue, 10) : Number(rawValue);
  }
  if (spec.value_type === "float") {
    return typeof rawValue === 'string' ? parseFloat(rawValue) : Number(rawValue);
  }
  if (spec.value_type === "boolean") {
    return Boolean(rawValue);
  }
  return rawValue;
}

// ... (singletonCache import removed from here)

export async function preloadDataTypes() {
  if (!singletonCache["data_types"]) {
    const res = await fetch(`${API_BASE}/data_types`);
    if (!res.ok) throw new Error(`GET /data_types → HTTP ${res.status}`);
    const json = await res.json();

    const data = (json.data_types as DataTypeSpec[]).map(dt => ({
      ...dt,
      label: normalizeLabel(dt.label)
    }));

    singletonCache["data_types"] = {
      promise: Promise.resolve(data),
      data,
      error: null,
    };
  }
}

export async function getOptions(source: string): Promise<OptionItem[]> {
  const cached = globalOptionsCache[source];
  if (cached) return cached;

  const existingPromise = optionsPromiseCache.get(source);
  if (existingPromise) return existingPromise;

  const fetchPromise = (async () => {
    try {
      const res = await fetch(`${API_BASE}/options/${encodeURIComponent(source)}`);
      if (!res.ok) throw new Error(`GET /options/${source} → HTTP ${res.status}`);
      const json = await res.json();
      const list = normalizeOptions(json?.options ?? []);
      globalOptionsCache[source] = list;
      notifyCacheListeners();
      return list;
    } finally {
      optionsPromiseCache.delete(source);
    }
  })();

  optionsPromiseCache.set(source, fetchPromise);
  return fetchPromise;
}

export function getOptionsFromCache(source: string): OptionItem[] | undefined {
  return globalOptionsCache[source];
}

export function getOptionsCacheSync(): Record<string, OptionItem[]> {
  return globalOptionsCache;
}
